import { API_BASE, FIRST_PARTY_APP_KEY } from './apiConfig';

// Domain method groups (merged onto prototype below)
import authMethods from './apiDomains/auth';
import nodeMethods from './apiDomains/nodes';
import thirdPartyMethods from './apiDomains/thirdParty';
import activityMethods from './apiDomains/activity';
import schemasMethods from './apiDomains/schemas';
import groupsMethods from './apiDomains/groups';
import pluginsMethods from './apiDomains/plugins';
import capabilitiesMethods from './apiDomains/capabilities';
import billingMethods from './apiDomains/billing';
import developerTokensMethods from './apiDomains/developerTokens';

class ApiService {
  _refreshing = null; // Singleton refresh promise to avoid parallel refreshes

  setToken(token) {
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }

  setRefreshToken(token) {
    if (token) {
      localStorage.setItem('refresh_token', token);
    } else {
      localStorage.removeItem('refresh_token');
    }
  }

  setApiKey(apiKey) {
    if (apiKey) {
      localStorage.setItem('apiKey', apiKey);
    } else {
      localStorage.removeItem('apiKey');
    }
  }

  getBaseUrl() { return API_BASE; }

  async request(endpoint, options = {}) {
    // Always read fresh token from localStorage on each request
    const token = localStorage.getItem('token');
    const apiKey = localStorage.getItem('apiKey');

    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    if (apiKey) {
      headers['X-API-Key'] = apiKey;
    }

    const config = {
      ...options,
      headers,
    };

    if (options.body && typeof options.body === 'object' && !(options.body instanceof FormData)) {
      config.body = JSON.stringify(options.body);
    }

    // Remove Content-Type for FormData
    if (options.body instanceof FormData) {
      delete headers['Content-Type'];
    }

    try {
      const response = await fetch(`${API_BASE}${endpoint}`, config);

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { message: 'Request failed' };
        }

        // Handle authentication errors — attempt refresh before logging out
        if (
          response.status === 401 &&
          !endpoint.startsWith('/auth/login') &&
          !endpoint.startsWith('/auth/refresh') &&
          !endpoint.startsWith('/auth/password/recovery-reset')
        ) {
          const refreshToken = localStorage.getItem('refresh_token');
          if (refreshToken) {
            try {
              const refreshed = await this._attemptRefresh(refreshToken);
              if (refreshed) {
                // Retry the original request with the new token
                return this.request(endpoint, options);
              }
            } catch {
              // Refresh failed — fall through to logout
            }
          }

          const hadActiveSession = sessionStorage.getItem('activeSession') === 'true';
          sessionStorage.removeItem('activeSession');
          localStorage.removeItem('refresh_token');

          window.dispatchEvent(new CustomEvent('auth:expired', {
            detail: {
              message: 'Your session has expired. Please log in again.',
              showExpiredMessage: hadActiveSession
            }
          }));
          return Promise.reject({ isAuthError: true, message: 'Session expired' });
        }

        // Extract error message from various formats. Some endpoints answer with
        // a top-level `errors` array (the DLR YAML parser, for one); without it
        // here every one of its messages was replaced by "HTTP 422".
        const errorMessage = errorData.error?.message ||
          errorData.message ||
          (Array.isArray(errorData.errors) && errorData.errors[0]) ||
          errorData.error?.code ||
          `HTTP ${response.status}: ${response.statusText}`;

        const err = new Error(errorMessage);
        err.code = errorData.error?.code;
        err.details = errorData.error?.details;
        err.status = response.status;
        // The parsed body, so callers can read structured errors rather than
        // re-deriving them from a flattened message.
        err.body = errorData;
        throw err;
      }

      // Handle empty responses
      const text = await response.text();
      const data = text ? JSON.parse(text) : {};

      return data;
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to server');
      }
      throw error;
    }
  }

  async _attemptRefresh(refreshToken) {
    // Deduplicate: if already refreshing, wait for that result
    if (this._refreshing) return this._refreshing;

    this._refreshing = (async () => {
      try {
        const resp = await fetch(`${API_BASE}/auth/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${refreshToken}`,
            'X-First-Party-Key': FIRST_PARTY_APP_KEY,
          },
        });
        if (!resp.ok) return false;
        const data = await resp.json();
        if (data.access_token) {
          this.setToken(data.access_token);
          // Store rotated refresh token (single-use rotation)
          if (data.refresh_token) {
            this.setRefreshToken(data.refresh_token);
          }
          return true;
        }
        return false;
      } catch {
        return false;
      } finally {
        this._refreshing = null;
      }
    })();

    return this._refreshing;
  }
}

// Merge domain methods onto the prototype
Object.assign(
  ApiService.prototype,
  authMethods,
  nodeMethods,
  thirdPartyMethods,
  activityMethods,
  schemasMethods,
  groupsMethods,
  pluginsMethods,
  capabilitiesMethods,
  billingMethods,
  developerTokensMethods,
);

export default new ApiService();
